import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Loader2, CheckCircle2, Clock } from "lucide-react";
import { logger } from "@/lib/logger";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { notifyDefectStatusChanged } from "@/lib/notifications";
import { TruncatedText } from "@/components/ui/truncated-text";

interface ResolveDefectDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defect: {
    id: string;
    title: string;
    created_by?: string | null;
    verifying_qm?: string | null;
  } | null;
  onResolved?: () => void;
}

export function ResolveDefectDialog({
  open,
  onOpenChange,
  defect,
  onResolved,
}: ResolveDefectDialogProps) {
  const { t } = useTranslation();

  const [saving, setSaving] = useState(false);
  const [resolutionNotes, setResolutionNotes] = useState("");
  const [hours, setHours] = useState(0);
  const [minutes, setMinutes] = useState(0);

  const handleSubmit = async () => {
    if (!defect) return;

    setSaving(true);
    try {
      const totalMinutes = hours * 60 + minutes;

      const { error } = await supabase
        .from("defects")
        .update({
          status: "resolved",
          resolution_notes: resolutionNotes || null,
          resolution_time_minutes: totalMinutes > 0 ? totalMinutes : null,
          resolved_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        })
        .eq("id", defect.id);

      if (error) throw error;

      // Notify verifying_qm that the defect is ready for verification
      // Use already-available props instead of re-fetching from DB
      const verifyingQm = defect.verifying_qm || defect.created_by;
      if (verifyingQm) {
        notifyDefectStatusChanged(verifyingQm, defect.title, defect.id, "resolved", {
          enableTeamsApproval: true,
        });
      }

      toast.success(t("defects.resolveDialog.successTitle"), {
        description: t("defects.resolveDialog.successDescription"),
      });

      resetForm();
      onOpenChange(false);
      onResolved?.();
    } catch (error) {
      logger.error("Error resolving defect:", error);
      toast.error(t("common.error"), {
        description: t("defects.resolveDialog.errorDescription"),
      });
    } finally {
      setSaving(false);
    }
  };

  const resetForm = () => {
    setResolutionNotes("");
    setHours(0);
    setMinutes(0);
  };

  const handleClose = () => {
    if (!saving) {
      resetForm();
      onOpenChange(false);
    }
  };

  const incrementHours = () => setHours((prev) => Math.min(prev + 1, 999));
  const decrementHours = () => setHours((prev) => Math.max(prev - 1, 0));
  const incrementMinutes = () => setMinutes((prev) => (prev >= 55 ? 0 : prev + 5));
  const decrementMinutes = () => setMinutes((prev) => (prev <= 0 ? 55 : prev - 5));

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[450px] w-[calc(100%-2rem)]">
        <DialogHeader className="bg-success text-success-foreground -mx-6 -mt-6 px-6 py-4 rounded-t-lg overflow-hidden">
          <DialogTitle className="flex items-center gap-2 text-white min-w-0">
            <CheckCircle2 className="h-5 w-5 shrink-0" />
            <TruncatedText
              text={
                defect?.title
                  ? t("defects.resolveDialog.title", { title: defect.title })
                  : t("defects.resolveDialog.titleFallback")
              }
              showTooltip={false}
            />
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Resolution Notes */}
          <div className="space-y-2">
            <Label htmlFor="resolution-notes" className="text-muted-foreground">
              {t("defects.resolveDialog.notesLabel")}
            </Label>
            <Textarea
              id="resolution-notes"
              value={resolutionNotes}
              onChange={(e) => setResolutionNotes(e.target.value)}
              placeholder={t("defects.resolveDialog.notesPlaceholder")}
              className="min-h-[80px]"
            />
          </div>

          {/* Time Input */}
          <div className="space-y-2">
            <Label className="text-muted-foreground">{t("defects.resolveDialog.timeLabel")}</Label>

            <div className="flex items-center gap-6">
              {/* Hours */}
              <div className="flex flex-col items-center">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={incrementHours}
                  className="h-8 w-8 p-0 min-h-[44px] min-w-[44px]"
                >
                  <span className="text-lg">∧</span>
                </Button>
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    value={hours}
                    onChange={(e) =>
                      setHours(Math.max(0, Math.min(999, parseInt(e.target.value) || 0)))
                    }
                    className="w-16 text-center"
                    min={0}
                    max={999}
                  />
                  <span className="text-sm text-muted-foreground">{t("common.hours")}</span>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={decrementHours}
                  className="h-8 w-8 p-0 min-h-[44px] min-w-[44px]"
                >
                  <span className="text-lg">∨</span>
                </Button>
              </div>

              {/* Minutes */}
              <div className="flex flex-col items-center">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={incrementMinutes}
                  className="h-8 w-8 p-0 min-h-[44px] min-w-[44px]"
                >
                  <span className="text-lg">∧</span>
                </Button>
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    value={minutes}
                    onChange={(e) =>
                      setMinutes(Math.max(0, Math.min(59, parseInt(e.target.value) || 0)))
                    }
                    className="w-16 text-center"
                    min={0}
                    max={59}
                  />
                  <span className="text-sm text-muted-foreground">{t("common.minutes")}</span>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={decrementMinutes}
                  className="h-8 w-8 p-0 min-h-[44px] min-w-[44px]"
                >
                  <span className="text-lg">∨</span>
                </Button>
              </div>
            </div>

            <p className="text-sm text-warning flex items-center gap-1">
              <Clock className="h-4 w-4" />
              {t("defects.resolveDialog.timeSpent", { hours, minutes })}
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={saving}>
            {t("common.cancel")}
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={saving}
            className="gap-2 bg-success hover:bg-success/90"
          >
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            {t("defects.resolveDialog.markResolved")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
